/**
*
* TopicItem
*
*/

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Badge, ListGroup } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import UserText from 'components/media/UserText/Loadable.js';
import DateTimeString from 'components/DateTimeString/index.jsx';
import Icon2 from 'components/media/Icon2/index.js';
import { SpinnerDiv } from 'components/Spinner/index.jsx';
import { PENDING_STATUS, SCHEDULE_DRAFT, SCHEDULE_SCHEDULED } from 'components/Post/constants.js';
import cx from 'utils/cx.js';
import { shorten } from 'utils/strings.js';
import ListParticipants from '../ListParticipants.jsx';
import messages from '../messages.js';
import useTopic from '../useTopic.jsx';
import StatusBadge from '../StatusBadge.jsx';

const MAXBODY = 72;
const MAXPARTICIPANTS = 30;
const TYPE_MESSAGE = 2;

const LimitWidth = styled.div`
  max-width: calc(100% - 46px);
`;

const MessageWrapper = styled.div`
  min-height: 3em;
`;

function TopicItem(props) {
  const { topic, active, query, disabled, loading, onClick, parentTopicId } = props;
  const { inboxId, archive } = useParams();
  const intl = useIntl();

  const {
    title, latestMessage, highlightedMessage, topicId, id, subTopicId, topicType, archived, status, scheduled, flagged,
  } = topic;
  const { createdBy, preview, modified, created, deleted } = latestMessage || highlightedMessage;
  const { ready, avatar, otherParticipants, wasLast } = useTopic(topic);
  if (!ready) {
    return null;
  }
  function getLastAuthor() {
    if (PENDING_STATUS.includes(status) || !createdBy) {
      return null;
    }
    return wasLast ? intl.formatMessage(messages.you) : createdBy.firstName;
  }
  const lastAuthor = getLastAuthor();
  const text = shorten(preview.replace(/\s+/g, ' '), MAXBODY);

  const conversationsLink = (parts) => `/conversations/inboxes/${inboxId}/${parts.filter(Boolean).join('/')}`;

  function getLinkProps() {
    if (onClick) {
      return { onClick };
    }
    const link = conversationsLink([archive, parentTopicId, topicId || id, subTopicId]);
    return {
      as: Link,
      to: query ? `${link}?q=${query}${topicType === TYPE_MESSAGE ? `#${id}` : ''}` : link,
    };
  }

  const hasUnseen = topic.unseenCount > 0;
  const maxParticipants = hasUnseen ? MAXPARTICIPANTS - 5 : MAXPARTICIPANTS;

  const statusBadge = (message, date) => (
    <div className="d-flex align-items-baseline">
      <StatusBadge className="ms-0 mt-2"><FormattedMessage {...message} /></StatusBadge>
      {date}
    </div>
  );
  // adapt lineheight with  .text-truncate to avoid vertical scrolling
  return (
    <ListGroup.Item className={cx('text-dark d-flex align-items-start', flagged && 'alert-warning topic-flagged')} disabled={disabled} active={active} action {...getLinkProps()}>
      <SpinnerDiv show={loading} />
      <div className="pt-1 pe-3">{avatar}</div>
      <LimitWidth className="flex-grow-1">
        <div className="d-flex justify-content-between">
          <ListParticipants className="small fw-semibold text-nowrap" participants={otherParticipants} limit={maxParticipants} markText={query} />
          <div className="text-nowrap">
            <DateTimeString className="small text-muted text-nowrap ms-1" date={modified || created} short />
            {flagged && <Icon2 className="ss-star fs-6 text-warning ms-2" />}
            {hasUnseen > 0 && <Badge variant="primary" className="ms-2">{topic.unseenCount}</Badge>}
          </div>
        </div>
        <div title={title}>
          { title
            ? <UserText inline markText={query} className="fw-semibold d-block text-truncate lh-base">{title}</UserText>
            : <span className="text-muted font-italic"><FormattedMessage {...messages.noTitle} /></span> }
        </div>
        <MessageWrapper>
          {lastAuthor && <UserText inline markText={query}>{`${lastAuthor}: `}</UserText>}
          { deleted
            ? <span className="text-muted font-italic"><FormattedMessage {...messages.deletedMessage} /></span>
            : <UserText inline markText={query}>{text}</UserText>}
          {Boolean(archived) && statusBadge(messages.archived)}
          {status === SCHEDULE_DRAFT && statusBadge(messages.draft)}
          {status === SCHEDULE_SCHEDULED && (
            statusBadge(messages.scheduled, scheduled
              ? <DateTimeString className="ms-2" date={scheduled} />
              : <span className="text-danger ms-2"><FormattedMessage {...messages.missingDate} /></span>)
          )}
        </MessageWrapper>
      </LimitWidth>
    </ListGroup.Item>
  );
}

TopicItem.propTypes = {
  topic: PropTypes.object.isRequired,
  parentTopicId: PropTypes.string,
  active: PropTypes.bool,
  query: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
};

export default TopicItem;
